type Child = Node | string | null | undefined | false;

/** Tiny hyperscript helper so screens can be built without a framework. */
export function h<K extends keyof HTMLElementTagNameMap>(
  tag: K,
  attrs: Record<string, string | boolean | number | ((e: Event) => void)> = {},
  ...children: (Child | Child[])[]
): HTMLElementTagNameMap[K] {
  const el = document.createElement(tag);
  for (const [k, v] of Object.entries(attrs)) {
    if (k.startsWith('on') && typeof v === 'function')
      el.addEventListener(k.slice(2).toLowerCase(), v);
    else if (k === 'class') el.className = String(v);
    else if (typeof v === 'boolean') {
      if (v) el.setAttribute(k, '');
      else el.removeAttribute(k);
      if (k in el) (el as unknown as Record<string, unknown>)[k] = v;
    } else el.setAttribute(k, String(v));
  }
  for (const c of children.flat()) {
    if (c === null || c === undefined || c === false) continue;
    el.append(typeof c === 'string' ? document.createTextNode(c) : c);
  }
  return el;
}

export function clear(el: HTMLElement): void {
  while (el.firstChild) el.removeChild(el.firstChild);
}

/** Keep wide tables scrollable without expanding the page. */
export function wrapTables(root: HTMLElement): void {
  for (const table of root.querySelectorAll('table')) {
    const wrap = h('div', {
      class: 'table-scroll',
      tabindex: 0,
      role: 'region',
      'aria-label': 'Results table',
    });
    table.replaceWith(wrap);
    wrap.append(table);
  }
}

export function ordinal(n: number): string {
  const s = ['th', 'st', 'nd', 'rd'];
  const v = n % 100;
  return n + (s[(v - 20) % 10] ?? s[v] ?? s[0]!);
}

/** Emoji per event for menus and cards. */
export const EVENT_EMOJI: Record<string, string> = {
  '100m': '🏃',
  lj: '🦘',
  sp: '🪨',
  hj: '🦵',
  '400m': '🔁',
  '110h': '🚧',
  dt: '🥏',
  pv: '🎋',
  jt: '🎯',
  '800m': '⏱️',
};

/** Human-readable label for a KeyboardEvent.code. */
export function keyLabel(code: string): string {
  if (code.startsWith('Key')) return code.slice(3);
  if (code.startsWith('Digit')) return code.slice(5);
  const map: Record<string, string> = {
    ArrowLeft: '←',
    ArrowRight: '→',
    ArrowUp: '↑',
    ArrowDown: '↓',
    Comma: ',',
    Period: '.',
    Space: 'Space',
    Semicolon: ';',
    Quote: "'",
    Slash: '/',
    BracketLeft: '[',
    BracketRight: ']',
    ShiftLeft: 'L Shift',
    ShiftRight: 'R Shift',
  };
  return map[code] ?? code;
}
